import { Box, Grid, lighten } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import Avatar from '../../../components/badges/Avatar'
import { EventPopupContent } from '../../popups/eventInfo/EventPopup'
import ObserveService from '../../../services/db/Observe'
import {
  AccountDoc,
  AggregatedDeliveryStatus,
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc,
  MasterLabelDoc,
  ParticipantDataDoc,
  ProfileDoc,
  TaskType,
  UserSettingsDoc
} from '../../../types/api/changes'
import { Model } from '../../../types/model'
import * as Styled from './styled'
import { eventStatusIcon } from '../../../utils/api/events'
import { useEffect, useRef } from 'react'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { CalendarRange } from 'types/components/calendar'
import { defaultGroupImage } from 'utils/api/groups'
import { APP_COLOR, isColorLight } from 'utils/colors'
import { isTrulyEmpty } from 'utils/object'
import { isTextRTL } from 'utils/lang'

import './styles.scss'
import { t } from 'i18next'
import dayjs from 'dayjs'
import dateToTimeString from '.'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { defaultTaskColor, provideTaskColor } from 'utils/masterLabel'
import { Notes, RecurrentItemCalendar, TypeNote, TypeTask } from 'assets/icons'
import SomedayItem from 'components/tfmDateSortedItems/SomedayItem'
import { CSSProperties } from 'styled-components'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import Item from 'components/tfmDateSortedItems/Item'
import { LocationLabel } from '../styled'
import {
  bookedEvent,
  eventIdForContainer,
  eventLengthInDays,
  provideEventTitle
} from 'utils/event'
import {
  getGroupColor,
  isConvertedGroup,
  isConvertedGroupId
} from 'utils/groups'

import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useTour } from '@reactour/tour'
import { colorFromMetaData } from 'utils/note_meta_data'
import { findStatusForInstance } from 'utils/recurrence'
import { isGroupInSupplementaryGroups } from 'utils/api/group_state'

import { useEventPopover } from '../eventPopover/EventPopoverProvider'
import Logger from 'services/Logger'
import { rtlString as fixStringForRtl } from 'utils/strings'
import { useCalendar } from 'components/providers/CalendarProvider'
import {
  getCachedConvertedCalendarsFromLocalStorage,
  thirdPartyCalendarIdToPrimaryEmail
} from 'utils/third_party_calendars'
import { darken } from '@material-ui/core'
import { isCypress } from 'utils/cypress'
import { isMobileDevice } from 'utils/constants'
import { CurrentConvertedCalendars } from 'utils/google_events'

export interface EventProps {
  topGroup?: Model<GroupDoc>
  title: string
  event?: Model<EventDoc> | EventDoc | undefined
  group?: Model<GroupDoc> | GroupDoc | undefined
  account?: Model<AccountDoc> | AccountDoc | undefined
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc | undefined
  masterLabel?: Model<MasterLabelDoc>
  instanceStart: number
  instanceEnd: number
  localParticipants?: Model<ParticipantDataDoc>[]
  profile?: Model<ProfileDoc>
  isStart?: boolean // for the long events, we need to know that we showing popup only for the start of the event
  closeAll?: boolean // for the drag/resize callbacks when we want to hide all opened popups
  alwaysFullSize?: boolean // to display event row fullsize always without checks which view is selected
  onEventDetailsClose?: () => void
  ignoreDim?: boolean
  calendarView?: CalendarRange
  doNotScrollToEvent?: boolean
  fc_id?: string
  dynamicDesign?: boolean
  isEnd?: boolean
  embedMode?: boolean
  searchMode?: boolean
}

interface DetermineEventColorParams {
  event?: Model<EventDoc> | EventDoc
  group?: Model<GroupDoc> | GroupDoc
  selectedGroup?: Model<GroupDoc> | GroupDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  taskDesign: boolean
  embedMode?: boolean
}

function determineEventColor(params: DetermineEventColorParams): string {
  const { event, group, selectedGroup, userSettings, taskDesign, embedMode } =
    params

  // Task design always uses white
  if (!isGroupcalApp() && taskDesign) {
    return 'white'
  }

  if (
    LocalStorage.get(LocalStorageKey.USER_ID) === null &&
    event?.GroupID !== group?._id
  ) {
    // we have forwareded event in read only, fallback to group color
    return selectedGroup?.GroupColor ?? APP_COLOR
  }

  // Embed mode uses group color or default
  if (embedMode) {
    return group?.GroupColor ?? APP_COLOR
  }

  // Default fallback color
  const defaultColor = !isTrulyEmpty(selectedGroup?.GroupColor)
    ? selectedGroup!.GroupColor
    : APP_COLOR

  if (!event) {
    return defaultColor
  }

  try {
    const groupsSettings = userSettings?.GroupsSettings

    // Check for member color in Groupcal app
    const memberColor =
      event.referenceGroup?.Participants?.[event.OwnerID ?? '']?.MemberColor

    if (!isTrulyEmpty(memberColor)) {
      return memberColor!
    }

    // 0. Metadata color for converted groups
    if (isConvertedGroup(group) && event) {
      const metadataColor = colorFromMetaData(event)
      if (metadataColor) {
        return metadataColor
      }
    }

    // Priority order for color determination:
    // 1. Event color (for 24me app or converted events)
    const hasEventColor =
      !isTrulyEmpty(event.Color) &&
      (is24meApp() || isConvertedGroupId(event.GroupID))

    // converted cached group color
    const colorFromCache = getCachedConvertedCalendarsFromLocalStorage().find(
      (calendar) => calendar.id === event.ThirdPartyID
    )?.backgroundColor

    if (isConvertedGroupId(event.GroupID) && colorFromCache) {
      return isCypress() ? colorFromCache : darken(colorFromCache, 0.15)
    }

    if (hasEventColor) {
      return event.Color!
    }

    // 2. Group settings color for this event's group
    const groupSettingsColor = groupsSettings?.[event.GroupID ?? '']?.GroupColor
    if (groupSettingsColor) {
      return groupSettingsColor
    }

    // 3. Reference group color
    const referenceGroupColor = getGroupColor(
      event.referenceGroup,
      groupsSettings
    )
    if (referenceGroupColor) {
      return referenceGroupColor
    }

    // 4. Selected group color
    const selectedGroupColor = getGroupColor(selectedGroup, groupsSettings)
    if (selectedGroupColor) {
      return selectedGroupColor
    }
  } catch (e) {
    console.error('Error determining event color:', e)
  }

  // Final fallback
  return defaultColor
}

function Event(props: EventProps) {
  const tfmTourProvider = useTfmTourProvider()
  const calendar = useCalendar()
  const tour = useTour()
  const eventView = calendar.view
  const { openPopover, currentEventId, closePopover } = useEventPopover()

  const taskDesign = isTaskDesign(props.event)

  const isPendingEvent =
    props.event?._id?.length === 0 &&
    props.isStart &&
    props.event?.local_id?.length === 0

  const initialOpenState = isPendingEvent

  const color = determineEventColor({
    event: props.event,
    group: props.group,
    selectedGroup: calendar.selectedGroup ?? undefined,
    userSettings: props.userSettings,
    taskDesign,
    embedMode: props.embedMode
  })

  function buildEventTime() {
    const formattedStart = eventStart.format('MMM D')
    const formattedEnd = eventEnd.format('MMM D')

    if (eventView === CalendarRange.MONTH) {
      if (props.event?.AllDay === '1') {
        return ''
      }
    }

    if (props.event?.AllDay === '1' && formattedStart === formattedEnd) {
      return t('allDay')
    }

    if (formattedStart !== formattedEnd) {
      return eventStart.format('MMM D') + ' - ' + eventEnd.format('MMM D')
    }

    return fixStringForRtl(
      `${
        props.event?.AllDay === '1'
          ? `${dateToTimeString(eventStart.toDate(), true).toLowerCase()}`
          : (() => {
              const startTime = dateToTimeString(
                eventStart.toDate(),
                true
              ).toLowerCase()
              const endTime = dateToTimeString(
                eventEnd.toDate(),
                true
              ).toLowerCase()

              // Extract the period (am/pm) from the start and end times
              const startPeriod = startTime.includes('am') ? 'am' : 'pm'
              const endPeriod = endTime.includes('am') ? 'am' : 'pm'

              // If the periods are the same, omit it from the start time
              if (startPeriod === endPeriod) {
                return `${startTime.replace('am', '').replace('pm', '')} - ${endTime}`
              }

              // Otherwise, include the full times
              return `${startTime} - ${endTime}`
            })()
      }`
    )
  }

  const eventStart = dayjs.unix(props.instanceStart / 1000)
  const eventEnd = dayjs.unix(props.instanceEnd / 1000)
  let eventSubText =
    eventView === CalendarRange.MONTH && !props.searchMode
      ? ''
      : buildEventTime()
  let fontSize =
    eventView === CalendarRange.MONTH && !props.searchMode ? '11.5px' : '12px'
  const dir =
    eventView === CalendarRange.MONTH && !props.searchMode
      ? 'ltr'
      : isTextRTL(props.event?.Text)
        ? 'rtl'
        : 'ltr' // Determine the text direction

  if (!props.event) {
    return (
      <Styled.EventContent
        sx={{
          border:
            eventView != CalendarRange.MONTH || taskDesign
              ? `1px solid ${taskDesign ? 'black' : 'white'}`
              : undefined
        }}
        padding={0}
        color={color}
        fontcolor={taskDesign ? 'black' : 'white'}
        fontSize={fontSize}
        style={{ direction: dir }}
        showstripes={undefined}
      >
        {eventView != CalendarRange.MONTH ? (
          <Box
            sx={{
              width: '100%',
              flexDirection: 'column',
              display: 'flex',
              color: taskDesign ? 'black' : 'white'
            }}
          >
            {t('newEvent')}
            <SubText text1={eventSubText} />
          </Box>
        ) : (
          <></>
        )}
      </Styled.EventContent>
    )
  }

  if (
    (props.event?._id?.length ?? 0) > 0 ||
    props.event?.AllDay === '1' ||
    props.event.late
  ) {
    eventSubText = isTrulyEmpty(props.event.Location?.Address)
      ? eventSubText
      : `${buildEventTime()}, ${props.event.Location?.Address}`
  }

  let direction =
    eventView === CalendarRange.MONTH ||
    props.event.AllDay === '1' ||
    props.event.late
      ? 'row-reverse'
      : 'column'

  let location =
    eventView !== CalendarRange.MONTH &&
    props.event.Location &&
    props.event.Location.Address != undefined

  const statusSize = provideStatusIconSize(props.event, eventView)
  const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID)
  const duration = Number(props.event.EndDate) - Number(props.event.StartDate)
  const allCalendarMode = !LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
  const ownerParticipant = props.localParticipants?.find(
    (p) => p.AccountID === props.event?.OwnerID
  )
  const isCurrentUserOwner = props.event.OwnerID === currentUserId

  const isGuest = !currentUserId

  const fullyVisibleEvent =
    props.embedMode ||
    props.event.GroupID === LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ||
    !LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ||
    LocalStorage.get(LocalStorageKey.SELECTED_GROUP)?.length === 0 ||
    isGroupInSupplementaryGroups(
      LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ?? '',
      props.event
    )
  const confirmStatus =
    props.event.ParticipantsStatus?.[
      LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
    ]?.ParticipantConfirmationStatus
  const pending =
    ((props.event._id?.length ?? 0) > 0 &&
      (props.event?.RequestConfirmation ?? '') === '1' &&
      props.event.TaskType !== TaskType.personalEvent &&
      (!confirmStatus || confirmStatus === '1' || confirmStatus === '4')) ||
    props.event?.attendees?.find(
      (attendee) =>
        attendee.self &&
        attendee.responseStatus?.toLowerCase() !== 'accepted' &&
        attendee.responseStatus?.toLowerCase() !== 'declined'
    ) != null

  const eventDuration = (props.instanceEnd - props.instanceStart) / 60000 //check if this less than 60, that means we have small event rectangle
  let smallEventGap = 59

  if (props.event.Location && typeof props.event.Location !== 'string')
    smallEventGap = 74

  const taskColor = provideTaskColor(
    props.masterLabel,
    props.event,
    props.account
  )

  let eventGroupColor =
    isGroupcalApp() &&
    props.event?.referenceGroup?.Participants?.[props.event?.OwnerID ?? '']
      ?.MemberColor
      ? (props.userSettings?.GroupsSettings?.[
          props.event?.referenceGroup?._id ?? ''
        ]?.GroupColor ?? props.event?.referenceGroup?.GroupColor)
      : undefined

  const currentConvertedCalendar =
    getCachedConvertedCalendarsFromLocalStorage().find(
      (cal) => cal.id === props.event?.ThirdPartyID
    )
  let colorDifferentThanCalendar = false

  if (!isTrulyEmpty(currentConvertedCalendar?.backgroundColor)) {
    const parentCalendarColor = currentConvertedCalendar?.backgroundColor!

    if (
      props.event.TaskType === TaskType.personalEvent &&
      darken(parentCalendarColor, 0.15) !== color
    ) {
      colorDifferentThanCalendar = true
      eventGroupColor = parentCalendarColor
    }
  }

  const hasLeftIndicator =
    isTaskDesign(props.event) ||
    eventGroupColor !== undefined ||
    colorDifferentThanCalendar

  let smallEvent =
    !props.alwaysFullSize &&
    (eventDuration < smallEventGap ||
      props.event.AllDay === '1' ||
      props.event.late ||
      eventView === CalendarRange.MONTH ||
      props.event._id?.length === 0)

  const containerStyle = {
    width: eventView === CalendarRange.MONTH ? '100%' : '100%',
    height:
      ((props.event.AllDay === '1' || props.event.late) &&
        (props.event.Type === DocType.TASK ||
          props.event.Type === DocType.NOTE)) ||
      eventView === CalendarRange.MONTH
        ? '18px !important'
        : '100%',
    minHeight: eventView === CalendarRange.MONTH ? undefined : '16px !important'
  }

  let isInAlldaySection =
    (props.event.AllDay === '1' || props.event.late) && !props.alwaysFullSize

  if (
    eventView === CalendarRange.LISTWEEK ||
    eventView === CalendarRange.LISTDAY
  ) {
    smallEvent = false
    isInAlldaySection = false
    direction = 'column'
    fontSize = '16px'
  }

  const pictureAndStatus = (
    <Grid
      item
      display={{ xs: 'none', lg: 'flex' }}
      flexDirection={direction === 'column' ? 'column' : 'row'}
      alignItems={'center'}
      height={'100%'}
    >
      <div>
        {!props.searchMode &&
        (eventView === CalendarRange.MONTH || props.embedMode || isGuest) ? (
          <></>
        ) : allCalendarMode || isConvertedGroup(props.event?.referenceGroup) ? (
          <Avatar
            name=""
            size="xxs"
            asGroup
            img={props.event?.referenceGroup?.Photo}
            emptyPic={defaultGroupImage(props.event?.referenceGroup)}
          />
        ) : (
          <Avatar
            asGroup={isGuest}
            emptyPic={defaultGroupImage(props.event?.referenceGroup)}
            img={
              isCurrentUserOwner
                ? props.profile?.ProfilePicture
                : (ownerParticipant?.PhotoURL ??
                  defaultGroupImage(props.event?.referenceGroup))
            }
            size="xxs"
            name={
              isCurrentUserOwner
                ? 'Y'
                : ((isTrulyEmpty(ownerParticipant?.FullName)
                    ? ownerParticipant?.PhoneNumber
                    : ownerParticipant?.FullName) ?? '')
            }
          />
        )}
      </div>
      {isConvertedGroupId(props.event.GroupID ?? '') ||
      props.embedMode ||
      isGuest ? (
        <></>
      ) : (
        <img
          style={{
            overflow: 'hidden'
          }}
          width={statusSize}
          height={statusSize}
          src={eventStatusIcon(props.event)}
        />
      )}
    </Grid>
  )

  const itemLeadingIcons = leadingIcons(props.event)

  const beforeNow = dayjs(props.instanceEnd).isBefore(dayjs().startOf('day'))
  const open = props.closeAll === true ? false : (initialOpenState ?? false)

  let eventTitle =
    props.title === bookedEvent
      ? t('bookedSlot')
      : (props.title ?? provideEventTitle(props.event))

  function makeEventTitle() {
    if (eventView === CalendarRange.MONTH && !props.searchMode) {
      const isRTL = isTextRTL(eventTitle) // Determine if the text is RTL

      return (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          width="100%"
          overflow="hidden"
          gap={1}
        >
          {/* Fixed-size div: Always on the left */}
          <div
            style={{
              flexShrink: 0 // Prevent shrinking
            }}
          >
            {props.event?.AllDay !== '1' && !isMobileDevice() && (
              <div style={{ fontWeight: 300 }}>
                {dateToTimeString(eventStart.toDate(), true).toLowerCase()}
              </div>
            )}
          </div>

          {/* Growing div: Handles text overflow */}
          <div
            style={{
              flexGrow: 1, // Allow the div to grow
              overflow: 'hidden', // Hide overflowing content
              textOverflow: 'hidden', // Add ellipsis for overflowing text
              whiteSpace: 'nowrap', // Prevent text wrapping
              direction: isRTL ? 'rtl' : 'ltr', // Set text direction dynamically
              textAlign: 'left' // Align text properly for RTL or LTR
            }}
          >
            {eventTitle}
          </div>
        </Box>
      )
    }

    return (
      <>{`${
        props.event?._id?.length === 0 && props.event?.local_id?.length === 0
          ? `${
              props.event.Type === DocType.TASK ? t('newTask') : t('newEvent')
            }`
          : eventTitle
      }${location && eventView == CalendarRange.LISTWEEK ? ', ' : ''}`}</>
    )
  }

  const eventRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTimeout(() => {
      if (
        props.event?._id?.length === 0 &&
        props.event?.local_id?.length === 0 &&
        eventRef &&
        LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) !== '1' &&
        LocalStorage.get(LocalStorageKey.DRAGGING) !== '1'
      ) {
        Logger.orange('WIll click', eventRef)
        eventRef?.current?.click()
      }
    }, 50)
  }, [eventRef, props.instanceStart, props.instanceEnd, props.event?._rev])
  return (
    <div
      id="event-root"
      style={{ height: '100%', width: '100%', zIndex: 1001 }}
    >
      {isTaskDesign(props.event) &&
      (eventView === CalendarRange.LISTWEEK ||
        eventView === CalendarRange.LISTDAY) ? (
        <Item
          item={props.event}
          title={props.title}
          hideTime
          setShowEvent={() => {}}
          withToggle
          account={props.account}
          userSettings={props.userSettings}
          event={props.event}
          open={false}
          setOpen={() => {}}
          instanceStart={props.instanceStart}
          instanceEnd={props.instanceEnd}
          onClose={props.onEventDetailsClose}
        />
      ) : (
        <Styled.EventContent
          id={eventIdForContainer(props.event, props.instanceStart)}
          data-color={color}
          data-rev={props.event._rev}
          sx={{
            ...containerStyle,
            zIndex: '1001 !important',
            position: 'relative',
            fontWeight:
              eventView === CalendarRange.WEEK ||
              eventView === CalendarRange.DAY
                ? 500
                : 400,
            border:
              eventView != CalendarRange.MONTH || taskDesign
                ? `1px solid ${taskDesign ? taskColor : 'white'}`
                : undefined,
            height:
              eventView === CalendarRange.LISTWEEK ||
              eventView === CalendarRange.LISTDAY
                ? '47px'
                : props.alwaysFullSize &&
                    (props.event.AllDay === '1' || props.event.late) &&
                    (props.event.Type === DocType.TASK ||
                      props.event.Type === DocType.NOTE)
                  ? '19px !important'
                  : '100%',
            minHeight:
              eventView === CalendarRange.LISTWEEK ||
              eventView === CalendarRange.LISTDAY ||
              props.alwaysFullSize
                ? '47px'
                : eventView === CalendarRange.MONTH
                  ? undefined
                  : '19px',
            backgroundColor:
              taskColor !== defaultTaskColor
                ? lighten(taskColor, 0.95)
                : 'undefined',
            width: '100%',
            borderRadius: (() => {
              if (!props.dynamicDesign) return '5px'

              if (
                !props.isStart &&
                !props.isEnd &&
                eventLengthInDays(props.event) > 1
              ) {
                return '0px' // No border-radius for the middle of the event
              } else if (props.isStart && eventLengthInDays(props.event) > 1) {
                return '5px 0 0 5px' // Very round border on the left side
              } else if (props.isEnd && eventLengthInDays(props.event) > 1) {
                return '0 5px 5px 0' // Very round border on the right side
              } else {
                return '5px' // Default border-radius
              }
            })()
          }}
          color={color}
          fontSize={fontSize}
          style={{ direction: dir }}
          fontcolor={
            isTaskDesign(props.event)
              ? '#000'
              : isColorLight(color ?? APP_COLOR, 0.8)
                ? '#000'
                : '#fff'
          }
          className={
            props.ignoreDim
              ? undefined
              : fullyVisibleEvent
                ? beforeNow
                  ? 'before-now'
                  : 'regular'
                : 'animate-opacity'
          }
          showstripes={pending && !props.embedMode ? 'true' : undefined}
          ref={eventRef}
          onMouseDown={() => {
            LocalStorage.set(LocalStorageKey.DRAGGING, '1')
          }}
          onMouseUp={() => {
            setTimeout(() => {
              LocalStorage.remove(LocalStorageKey.DRAGGING)
            }, 25)
          }}
          onClick={(e) => {
            // e.preventDefault()
            // e.stopPropagation()
            console.log('Click called for event', props.event?.Text, e)
            if (props.event?.someday === '1') {
              return
            }
            if (LocalStorage.get(LocalStorageKey.VISIBLE_EVENT)) {
              if (
                LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) === null
              ) {
                closePopover()
              }
              return
            }
            if (fullyVisibleEvent || props.ignoreDim) {
              openPopover(
                eventIdForContainer(props.event, props.instanceStart) ??
                  props.event?.id ??
                  '',
                <EventPopupContent
                  open={false}
                  awayClickTriggered={false}
                  setAwayClickTriggered={function (b: boolean): void {}}
                  event={props.event!}
                  group={props.group}
                  showSmallUI={isGuest || (props.embedMode ?? false)}
                  userSettings={props.userSettings!}
                  account={props.account!}
                  onClose={() => {}}
                  instanceStart={props.instanceStart}
                  instanceEnd={props.instanceEnd}
                  fc_id={props.fc_id ?? ''}
                />,
                false,
                undefined,
                e.currentTarget
              )
            } else {
              const divsToAnimate =
                document.querySelectorAll('.animate-opacity')
              divsToAnimate.forEach(
                (div) => ((div as any).style.opacity = beforeNow ? 0.6 : 1)
              )
              const animationTimeout = setTimeout(() => {
                divsToAnimate.forEach(
                  (div) => ((div as any).style.opacity = '0.1')
                )
              }, 2000)

              setTimeout(() => {
                if (tour.isOpen) {
                  // do not open dimmed in case of other is active
                  return
                }

                tfmTourProvider.setMode(ONBOARDING_MODE.DIMMED)
                if (tour.setSteps && tfmTourProvider.getStepsByMode) {
                  tour.setSteps(
                    tfmTourProvider.getStepsByMode(ONBOARDING_MODE.DIMMED)
                  )
                }

                tour.setCurrentStep(0)
                tour.setIsOpen(true)
              }, 2000)
            }
          }}
        >
          {hasLeftIndicator && !props.embedMode && (
            <Grid
              item
              id={`${eventIdForContainer(props.event, props.instanceStart)}-indicator`}
              data-indicator-color={eventGroupColor}
              xs={'auto'}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              style={{
                width: '4px',
                borderTopLeftRadius: dir === 'rtl' ? 0 : 8,
                borderBottomLeftRadius: dir === 'rtl' ? 0 : 8,
                borderTopRightRadius: dir === 'rtl' ? 8 : 0,
                borderBottomRightRadius: dir === 'rtl' ? 8 : 0,
                overflow: 'hidden',
                backgroundColor: isGroupcalApp()
                  ? eventGroupColor
                    ? darken(eventGroupColor, 0.15)
                    : undefined
                  : taskColor
              }}
            />
          )}
          <Grid
            xs
            item
            height={'100%'}
            overflow={'hidden'}
            paddingLeft={
              eventView === CalendarRange.MONTH ? '0.15rem' : '0.3rem'
            }
            paddingRight={
              eventView === CalendarRange.MONTH ? '0.15rem' : '0.3rem'
            }
            paddingTop={
              eventView === CalendarRange.MONTH || isInAlldaySection
                ? 0
                : '0.15rem'
            }
          >
            <Grid
              overflow={'hidden'}
              display={'flex'}
              width={'100%'}
              flexDirection={'column'}
            >
              {props.event.someday === '1' ? (
                <Grid
                  container
                  display={'flex'}
                  wrap="nowrap"
                  alignItems={
                    isInAlldaySection || eventView === CalendarRange.MONTH
                      ? 'center'
                      : undefined
                  }
                  flexDirection={smallEvent ? 'row' : 'column'}
                >
                  <SomedayItem title={props.event.Text} />
                </Grid>
              ) : (
                <GroupcalTooltip
                  placement="top"
                  hidden={props.event.Text?.length === 0}
                  enterDelay={200}
                  title={
                    <Box
                      display={'flex'}
                      flexDirection={'column'}
                      alignItems={dir === 'rtl' ? 'end' : 'start'}
                      lineHeight={'18px'}
                    >
                      <Box gap={1} display={'flex'} flexDirection={'row'}>
                        {itemLeadingIcons}
                        {makeEventTitle() ?? ''}
                      </Box>
                      <div>{buildEventTime()}</div>
                      <div>{props.event?.Location?.Address}</div>
                    </Box>
                  }
                >
                  <Grid
                    container
                    display={'flex'}
                    wrap="nowrap"
                    alignItems={
                      !props.searchMode &&
                      (isInAlldaySection || eventView === CalendarRange.MONTH)
                        ? 'center'
                        : undefined
                    }
                    flexDirection={
                      !props.searchMode && smallEvent ? 'row' : 'column'
                    }
                  >
                    <Grid
                      item
                      xs
                      gap={1}
                      flexDirection={'row'}
                      display={'flex'}
                      color={props.event.Priority == '2' ? 'red' : undefined}
                      style={{
                        transform:
                          eventView === CalendarRange.LISTWEEK ||
                          eventView === CalendarRange.LISTDAY
                            ? 'translateY(-1px)'
                            : undefined,
                        lineHeight:
                          eventView === CalendarRange.LISTWEEK ||
                          eventView === CalendarRange.LISTDAY
                            ? undefined
                            : '16px',
                        textDecoration: isInstanceCompleted(
                          props.event,
                          props.instanceStart
                        )
                          ? 'line-through'
                          : 'none'
                      }}
                    >
                      {makeEventTitle()}
                      {eventView === CalendarRange.LISTWEEK && (
                        <LocationLabel>
                          {location ? `${props.event.Location?.Address}` : ''}
                        </LocationLabel>
                      )}
                    </Grid>
                  </Grid>
                </GroupcalTooltip>
              )}
              {eventView !== CalendarRange.LISTWEEK &&
                (location || eventSubText) &&
                props.event.AllDay !== '1' &&
                !props.event.late && (
                  <div
                    style={{
                      color:
                        props.event.TaskType === TaskType.personalEvent ||
                        props.event.Type === DocType.EVENT
                          ? isColorLight(color ?? APP_COLOR, 0.8)
                            ? '#000'
                            : '#fff'
                          : '#000',
                      fontSize: '12px',
                      lineHeight: '16px',
                      fontWeight: 300
                    }}
                  >
                    {(eventView != CalendarRange.MONTH || props.searchMode) &&
                      eventSubText}
                  </div>
                )}
            </Grid>
          </Grid>
          <Grid
            item
            xs={'auto'}
            paddingRight={'0.3rem'}
            paddingLeft={'0.3rem'}
            paddingTop={isInAlldaySection ? 0 : '0.15rem'}
          >
            {isGroupcalApp() ? pictureAndStatus : <></>}
          </Grid>
        </Styled.EventContent>
      )}
    </div>
  )
}

function provideStatusIconSize(
  event: Model<EventDoc> | EventDoc | undefined,
  eventView: CalendarRange
) {
  if (!event) return 0
  const {
    AggregatedParticipantsDeliveryStatus,
    AggregatedParticipantsConfirmationStatus
  } = event

  const regularSize = eventView === CalendarRange.MONTH ? 9 : 10

  const seenByAllSize = eventView === CalendarRange.MONTH ? 12 : 14

  if (
    AggregatedParticipantsConfirmationStatus != null &&
    AggregatedParticipantsConfirmationStatus.length > 0
  )
    return seenByAllSize

  if (AggregatedParticipantsDeliveryStatus != null) {
    if (
      Number(AggregatedParticipantsDeliveryStatus) >=
      Number(AggregatedDeliveryStatus.DELIVERED)
    ) {
      return seenByAllSize
    } else {
      return regularSize
    }
  }

  return regularSize
}

interface SubTextProps {
  text1?: string | undefined | null
  text2?: string | undefined | null
}

export function SubText(props: SubTextProps) {
  return (
    <div
      style={{
        marginTop: '3px',
        fontSize: '12px',
        fontWeight: 300,
        overflow: 'hidden',
        lineHeight: '13px',
        whiteSpace: 'pre-line',
        textOverflow: 'ellipsis'
      }}
    >
      {props.text1}
      {props.text2}
    </div>
  )
}

export default withObservables(['event'], (props: EventProps) => ({
  account: ObserveService.Account(),
  group: ObserveService.GroupByServerIDRestricted(
    props.event?.GroupID ?? LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
  ),
  userSettings: ObserveService.UserSettings(),
  topGroup: ObserveService.FirstGroup(props.userSettings),
  profile: ObserveService.Profile(),
  localParticipants: ObserveService.Participants(),
  masterLabel: ObserveService.MasterLabel()
}))(Event as any)

export function leadingIcons(event: EventDoc) {
  if (!event.Type) return <></>

  const size = 10
  const color = 'white'
  let icon
  switch (event.Type as DocType) {
    case DocType.EVENT:
      icon = undefined
      break
    case DocType.TASK:
      icon = <TypeTask color="white" width={size} height={size} />
      break
    case DocType.NOTE:
      icon = <TypeNote color="white" width={size} height={size} />
      break
  }

  const divStyle: CSSProperties = {}

  const hasIcons =
    icon ||
    (event.Recurrence &&
      typeof event.Recurrence !== 'string' &&
      Object.keys(event.Recurrence).length != 0) ||
    (event.Type != DocType.NOTE &&
      event.Notes &&
      typeof event.Notes !== 'string' &&
      event.Notes.length > 0)

  if (!hasIcons) return <></>

  return (
    <Grid item display={'flex'} flexDirection={'row'} gap={'0.25rem'}>
      {icon && <div style={divStyle}>{icon}</div>}
      {event.Recurrence &&
        typeof event.Recurrence !== 'string' &&
        Object.keys(event.Recurrence).length != 0 && (
          <div style={divStyle}>
            <RecurrentItemCalendar color={color} width={size} height={size} />
          </div>
        )}
      {event.Type != DocType.NOTE &&
        event.Notes &&
        typeof event.Notes !== 'string' &&
        event.Notes.length > 0 && (
          <div style={divStyle}>
            <Notes width={size} height={size} />
          </div>
        )}
    </Grid>
  )
}

function isTaskDesign(event?: EventDoc): boolean {
  if (!event) {
    return is24meApp()
  }

  return event.Type === DocType.TASK || event.Type === DocType.NOTE
}

export function isInstanceCompleted(
  event: EventDoc,
  instanceStart: number
): boolean {
  if (isGroupcalApp()) return false

  const statusAsString = String(event.Status)

  if (!event.Recurrence) return statusAsString === EventStatus.COMPLETED

  if (statusAsString !== EventStatus.ACTIVE) return true

  if (
    event.Recurrence &&
    event.Recurrence.Exclusion &&
    typeof event.Recurrence.Exclusion !== 'string'
  ) {
    const instance = event.Recurrence.Exclusion?.find(
      (exclusion) =>
        dayjs.unix(Number(exclusion.Date)).format('YYYY-MM-DD') ===
        dayjs(instanceStart).format('YYYY-MM-DD')
    )

    return (
      String(findStatusForInstance(event, instanceStart)) ===
      EventStatus.COMPLETED
    )
  }

  return false
}

export function instanceStatus(
  event: EventDoc,
  instanceStart: number
): EventStatus {
  if (!event.Recurrence) return event.Status ?? EventStatus.ACTIVE

  if (event.Status !== EventStatus.ACTIVE)
    return event.Status ?? EventStatus.ACTIVE

  if (
    event.Recurrence &&
    event.Recurrence.Exclusion &&
    typeof event.Recurrence.Exclusion !== 'string'
  ) {
    const instance = event.Recurrence.Exclusion?.find(
      (exclusion) =>
        dayjs.unix(Number(exclusion.Date)).format('YYYY-MM-DD') ===
        dayjs(instanceStart).format('YYYY-MM-DD')
    )
    return String(findStatusForInstance(event, instanceStart)) as EventStatus
  }

  return event.Status ?? EventStatus.ACTIVE
}
